import { ChainCategorySection, ChainsFilterBar, ChainsListTable, DeleteChainDialog, StartChainDialog } from '@/components/projects/chains'
import { ProjectSectionHeader } from '@/components/projects/ProjectSectionHeader'
import { Button } from '@/components/ui/button'
import { useAuth, useChains, useDebounce, useDuplicateChain, useToggleChainActive } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { CHAIN_DEFINITION_CATEGORIES, type ChainDefinitionCategory, type ChainDefinitionFilters, type ChainDefinitionSummary } from '@/types'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

const UNCATEGORIZED = 'Uncategorized' as const
type SectionCategory = ChainDefinitionCategory | typeof UNCATEGORIZED
const CATEGORY_ORDER: SectionCategory[] = [...CHAIN_DEFINITION_CATEGORIES, UNCATEGORIZED]
const DEFAULT_FILTERS: ChainDefinitionFilters = { search: '', status: 'active', type: 'all', category: 'all' }

export function ChainsPage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)

  const [filters, setFilters] = useState<ChainDefinitionFilters>(DEFAULT_FILTERS)
  const debouncedSearch = useDebounce(filters.search)
  const [sectionExpanded, setSectionExpanded] = useState<Record<SectionCategory, boolean>>(
    () => Object.fromEntries(CATEGORY_ORDER.map((c) => [c, true])) as Record<SectionCategory, boolean>,
  )
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    try {
      return sessionStorage.getItem(`chains-view-mode-${projectId}`) === 'list' ? 'list' : 'grid'
    } catch {
      return 'grid'
    }
  })

  const [deleteTarget, setDeleteTarget] = useState<ChainDefinitionSummary | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [startTarget, setStartTarget] = useState<ChainDefinitionSummary | null>(null)
  const [startOpen, setStartOpen] = useState(false)

  const { data, isLoading, isError, refetch } = useChains(projectId!)
  const allChains = data?.items ?? []
  const duplicateChain = useDuplicateChain(projectId!)
  const toggleActive = useToggleChainActive(projectId!)

  const filtered = useMemo(() => {
    const s = debouncedSearch.trim().toLowerCase()
    return allChains.filter((c) => {
      if (s && !c.displayName.toLowerCase().includes(s) && !c.name.toLowerCase().includes(s) && !c.description?.toLowerCase().includes(s)) return false
      if (filters.status === 'active' && !c.isActive) return false
      if (filters.status === 'inactive' && c.isActive) return false
      if (filters.type === 'system' && !c.isSystem) return false
      if (filters.type === 'custom' && c.isSystem) return false
      if (filters.category !== 'all' && c.category !== filters.category) return false
      return true
    })
  }, [allChains, debouncedSearch, filters])

  const grouped = useMemo(() => {
    const map = new Map<SectionCategory, ChainDefinitionSummary[]>()
    for (const cat of CATEGORY_ORDER) map.set(cat, [])
    for (const c of filtered) {
      const bucket = c.category && map.has(c.category) ? c.category : UNCATEGORIZED
      map.get(bucket)!.push(c)
    }
    return map
  }, [filtered])

  if (authLoading) return null
  if (!hasPermission(Permissions.Project.Chain.View, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  const canCreate = hasPermission(Permissions.Project.Chain.Create, projectId)
  const canToggle = hasPermission(Permissions.Project.Chain.Edit, projectId)
  const canDelete = hasPermission(Permissions.Project.Chain.Delete, projectId)
  const canStart = hasPermission(Permissions.Project.Chain.Start, projectId)

  const handleNavigate = (c: ChainDefinitionSummary) => navigate(`/projects/${projectId}/chains/${c.id}`)
  const handleCreate = () => navigate(`/projects/${projectId}/chains/new`)

  const handleDuplicate = async (c: ChainDefinitionSummary) => {
    try {
      const r = await duplicateChain.mutateAsync(c.id)
      toast.success(`Duplicated "${c.displayName}"`)
      navigate(`/projects/${projectId}/chains/${(r as { chainDefinitionId: string }).chainDefinitionId}`)
    } catch {
      toast.error('Failed to duplicate chain')
    }
  }

  const handleToggle = async (c: ChainDefinitionSummary) => {
    try {
      await toggleActive.mutateAsync(c.id)
      toast.success(`${c.isActive ? 'Disabled' : 'Enabled'} "${c.displayName}"`)
    } catch {
      toast.error('Failed to toggle chain status')
    }
  }

  const handleDelete = (c: ChainDefinitionSummary) => {
    setDeleteTarget(c)
    setDeleteOpen(true)
  }

  const handleStart = (c: ChainDefinitionSummary) => {
    setStartTarget(c)
    setStartOpen(true)
  }

  const handleFilterChange = (key: keyof ChainDefinitionFilters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }))
  const handleClearFilters = () => setFilters(DEFAULT_FILTERS)
  const handleToggleSection = (cat: SectionCategory) =>
    setSectionExpanded((prev) => ({ ...prev, [cat]: !prev[cat] }))
  const handleViewModeChange = (mode: 'grid' | 'list') => {
    setViewMode(mode)
    try { sessionStorage.setItem(`chains-view-mode-${projectId}`, mode) } catch { /* ignore */ }
  }

  const cardProps = {
    canStart,
    canDuplicate: canCreate,
    canToggle,
    canDelete,
    onNavigate: handleNavigate,
    onStart: handleStart,
    onDuplicate: handleDuplicate,
    onToggleActive: handleToggle,
    onDelete: handleDelete,
  }

  return (
    <div className="space-y-6" data-testid="chains-page">
      <ProjectSectionHeader sectionName="Chains">
        {canCreate && (
          <Button onClick={handleCreate} data-testid="new-chain-button">
            <Plus className="h-4 w-4" />
            New Chain
          </Button>
        )}
      </ProjectSectionHeader>

      <div className="flex items-center justify-between gap-4">
        <ChainsFilterBar filters={filters} onFilterChange={handleFilterChange} onClearAll={handleClearFilters} viewMode={viewMode} onViewModeChange={handleViewModeChange} />
      </div>

      {isError ? (
        <div className="text-center py-12 text-muted-foreground" role="alert" data-testid="chains-error">
          Failed to load chains.{' '}
          <button className="underline" onClick={() => refetch()}>Retry</button>
        </div>
      ) : isLoading ? (
        <div className="text-center py-12 text-muted-foreground" role="status" aria-live="polite" data-testid="chains-loading">Loading chains…</div>
      ) : allChains.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="chains-empty">
          No chains yet. Create your first chain.
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="chains-no-match">No chains match your filters.</div>
      ) : viewMode === 'list' ? (
        <ChainsListTable chains={filtered} {...cardProps} />
      ) : (
        <div className="space-y-2" data-testid="chains-categories">
          {CATEGORY_ORDER.map((cat) => {
            const items = grouped.get(cat) ?? []
            if (items.length === 0) return null
            return (
              <ChainCategorySection
                key={cat}
                category={cat}
                chains={items}
                isExpanded={sectionExpanded[cat]}
                onToggle={() => handleToggleSection(cat)}
                {...cardProps}
              />
            )
          })}
        </div>
      )}

      <DeleteChainDialog projectId={projectId!} chain={deleteTarget} open={deleteOpen} onOpenChange={setDeleteOpen} />
      <StartChainDialog projectId={projectId!} chain={startTarget} open={startOpen} onOpenChange={setStartOpen} />
    </div>
  )
}
